import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';

function AccountManagement() {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form State
  const [showModal, setShowModal] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [accountType, setAccountType] = useState('ADVERTISER');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('Jharkhand');
  const [billingStateCode, setBillingStateCode] = useState('20');
  const [billingPincode, setBillingPincode] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/accounts`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setAccounts(await res.json());
        setError(null);
      } else {
        setError('Failed to fetch advertiser accounts');
      }
    } catch (e) {
      setError('Network error fetching advertiser accounts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!legalName.trim()) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/accounts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          legal_name: legalName,
          display_name: displayName || legalName,
          account_type: accountType,
          gstin: gstin || null,
          pan: pan || null,
          billing_address: billingAddress || null,
          billing_city: billingCity || null,
          billing_state: billingState || null,
          billing_state_code: billingStateCode || null,
          billing_pincode: billingPincode || null,
          contact_email: contactEmail || null,
          contact_phone: contactPhone || null
        })
      });

      if (res.ok) {
        setShowModal(false);
        setLegalName('');
        setDisplayName('');
        setGstin('');
        setPan('');
        setContactEmail('');
        setContactPhone('');
        fetchAccounts();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to register advertiser account');
      }
    } catch (e) {
      alert('Network error registering advertiser account');
    }
  };

  if (loading) {
    return <div className="card"><p>Loading Advertiser Accounts...</p></div>;
  }

  return (
    <div>
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Header Actions */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>Advertisers & Commercial Accounts</h3>
            <p style={{ color: '#666', marginTop: '4px' }}>Register business legal profiles, corporate GSTIN tax IDs, and billing contacts.</p>
          </div>
          <button className="btn-primary" onClick={() => setShowModal(true)}>+ Register Advertiser Account</button>
        </div>
      </div>

      {/* Accounts List */}
      <div className="card">
        {accounts.length === 0 ? (
          <p style={{ padding: '16px', color: '#666' }}>No commercial accounts registered yet. Click button above to register.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Legal Name</th>
                <th>Type</th>
                <th>GSTIN / PAN</th>
                <th>City / State</th>
                <th>Contact</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((acc) => (
                <tr key={acc.id}>
                  <td>
                    <strong>{acc.legal_name}</strong>
                    {acc.display_name && acc.display_name !== acc.legal_name && (
                      <div style={{ fontSize: '12px', color: '#666' }}>({acc.display_name})</div>
                    )}
                  </td>
                  <td><span className="badge-info">{acc.account_type}</span></td>
                  <td>
                    {acc.gstin ? <div>GST: <code>{acc.gstin}</code></div> : null}
                    {acc.pan ? <div>PAN: <code>{acc.pan}</code></div> : null}
                    {!acc.gstin && !acc.pan && <span style={{ color: '#999' }}>Unregistered</span>}
                  </td>
                  <td>{acc.billing_city ? `${acc.billing_city}, ${acc.billing_state || ''}` : 'N/A'}</td>
                  <td>
                    {acc.contact_phone && <div>📞 {acc.contact_phone}</div>}
                    {acc.contact_email && <div>✉️ {acc.contact_email}</div>}
                  </td>
                  <td><span className="badge-success">{acc.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '600px', background: '#fff', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '16px' }}>Register Commercial Account</h3>
            <form onSubmit={handleCreateAccount}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Legal Entity Name *</label>
                  <input type="text" placeholder="e.g. Shree Ram Enterprises Pvt Ltd" value={legalName} onChange={(e) => setLegalName(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Account Type</label>
                  <select value={accountType} onChange={(e) => setAccountType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                    <option value="ADVERTISER">Brand Advertiser</option>
                    <option value="FACILITY_PROVIDER">Facility Provider</option>
                    <option value="MANDIR_TRUST">Mandir / Temple Trust</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>GSTIN Number</label>
                  <input type="text" placeholder="e.g. 10AAAAA0000A1Z5" value={gstin} onChange={(e) => setGstin(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>PAN Number</label>
                  <input type="text" placeholder="e.g. ABCDE1234F" value={pan} onChange={(e) => setPan(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Contact Email</label>
                  <input type="email" placeholder="billing@company.com" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Contact Phone</label>
                  <input type="text" placeholder="+91 9876543210" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Billing Address</label>
                <input type="text" placeholder="Street / Premises address" value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>City</label>
                  <input type="text" placeholder="Deoghar" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>State</label>
                  <input type="text" value={billingState} onChange={(e) => setBillingState(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Pincode</label>
                  <input type="text" placeholder="814112" value={billingPincode} onChange={(e) => setBillingPincode(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Register Account</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountManagement;
